import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'antd';
import { useI18nFormatters } from 'hooks/i18nFormatters';
import CountryFlag from 'components/CountryFlag';
import CompanySeparator from '../../../../Separator';

import './styles.less';

const I18N_BASE_PATH =
  'pages.private.KYBCompany.pages.companyDetails.subPages.companyInfo';

const CompanyIdentificationInformationSection = ({ creditReportData }) => {
  const { t: translate } = useTranslation();
  const { i18nFormatDateOnly } = useI18nFormatters();

  return (
    <div className="section">
      <div className="title">
        {translate(
          `${I18N_BASE_PATH}.creditReport.companyIdentificationInformation.title`,
          'Company identification information'
        )}
      </div>
      <div className="text-dark text-bold font-size-20 mrg-top-10">
        {creditReportData.name}
        <CountryFlag
          country={creditReportData.country}
          className="mrg-left-15 country-flag"
        />
      </div>
      <Row className="content">
        <Col span={12} className="flex-column-content">
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.companyIdentificationInformation.columns.number`,
                  'Company number'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.number || '-'}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.companyIdentificationInformation.columns.name`,
                  'Company name'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.name}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.companyIdentificationInformation.columns.status`,
                  'Company status'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.status}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.companyIdentificationInformation.columns.incorporationDate`,
                  'Incorporation data'
                )}
              </strong>
            </Col>
            <Col className="col-info">
              {i18nFormatDateOnly(creditReportData.registrationDate || '', 1)}
            </Col>
          </Row>
        </Col>
        <Col span={12} className="flex-column-content">
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.companyIdentificationInformation.columns.type`,
                  'Company type'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.companyType}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.companyIdentificationInformation.columns.VATNumber`,
                  'VAT number'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.vatNumber}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.companyIdentificationInformation.columns.code`,
                  'SIC07 Code'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.sic07Code}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.companyIdentificationInformation.columns.codeDescription`,
                  'SIC07 Description'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.sic07Description}</Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

const AddressAndContactDetailsSection = ({ creditReportData, openAddressModal }) => {
  const { t: translate } = useTranslation();

  return (
    <div className="section">
      <div className="title">
        {translate(
          `${I18N_BASE_PATH}.creditReport.addressAndContactDetails.title`,
          'Address and Contact details'
        )}
      </div>
      <Row className="content">
        <Col span={24} className="flex-column-content">
          <Row>
            <Col span={3} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.addressAndContactDetails.columns.telephone`,
                  'Telephone'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.telephone}</Col>
          </Row>
          <Row>
            <Col span={3} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.addressAndContactDetails.columns.email`,
                  'Email'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.email}</Col>
          </Row>
          <Row>
            <Col span={3} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.addressAndContactDetails.columns.url`,
                  'URL'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.url}</Col>
          </Row>
          <Row>
            <Col span={3} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.addressAndContactDetails.columns.address`,
                  'Address'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.address}</Col>
          </Row>
          <Row className="company-print-no-render">
            <Col span={6} className="no-pdd">
              <a
                href="/#"
                rel="noreferrer"
                className="text-bold company-print-no-render"
                onClick={(e) => {
                  e.preventDefault();
                  openAddressModal({
                    addresses: creditReportData.otherAddresses,
                  });
                }}
              >
                {translate(
                  `${I18N_BASE_PATH}.creditReport.addressAndContactDetails.otherKnownAddresses`,
                  'Other Known Addresses'
                )}
              </a>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

const AdditionalInformationSection = ({ creditReportData }) => {
  const { t: translate } = useTranslation();
  const {
    i18nFormatDateOnly,
    i18nFormatCurrency,
    i18nFormatNumber,
  } = useI18nFormatters();

  const shareCapitalValue = useMemo(() => {
    if (!creditReportData.shareCapitalValue) return '-';
    if (creditReportData.shareCapitalCurrency) {
      return i18nFormatCurrency(
        creditReportData.shareCapitalValue,
        null,
        creditReportData.shareCapitalCurrency
      );
    }
    return i18nFormatNumber(creditReportData.shareCapitalValue);
  }, [creditReportData]);

  return (
    <div className="section">
      <div className="title">
        {translate(
          `${I18N_BASE_PATH}.creditReport.additionalInformation.title`,
          'Additional Information'
        )}
      </div>
      <Row className="content">
        <Col span={12} className="flex-column-content">
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.FTSEIndex`,
                  'FTSE Index'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.ftseIndex}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.shareCapital`,
                  'Share Capital'
                )}
              </strong>
            </Col>
            <Col className="col-info">{shareCapitalValue}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.currency`,
                  'Currency'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.shareCapitalCurrency || '-'}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.charges`,
                  'Charges'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.charges}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.landRegistry`,
                  'Land Registry'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.landRegistry}</Col>
          </Row>
          <Row>
            <Col span={6} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.principalActivity`,
                  'Principal Activity'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.mainActivity}</Col>
          </Row>
        </Col>
        <Col span={12} className="flex-column-content">
          <Row>
            <Col span={8} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.filingDateOfAccounts`,
                  'Filing Date of accounts'
                )}
              </strong>
            </Col>
            <Col className="col-info">
              {i18nFormatDateOnly(creditReportData.filingDateOfAccounts, 1)}
            </Col>
          </Row>
          <Row>
            <Col span={8} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.charityNumber`,
                  'Charity Number'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.charityNumber}</Col>
          </Row>
          <Row>
            <Col span={8} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.accountant`,
                  'Accountant'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.accountant}</Col>
          </Row>
          <Row>
            <Col span={8} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.tradeDebtors`,
                  'Trade Debtors'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.tradeDebtors}</Col>
          </Row>
          <Row>
            <Col span={8} className="col-info">
              <strong>
                {translate(
                  `${I18N_BASE_PATH}.creditReport.additionalInformation.columns.TradeCreditors`,
                  'Trade Creditors'
                )}
              </strong>
            </Col>
            <Col className="col-info">{creditReportData.tradeCreditors}</Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

const CreditReportComponent = ({ openAddressModal, creditReportData }) => {
  return (
    <>
      <div className="company-info-credit-report">
        <CompanyIdentificationInformationSection creditReportData={creditReportData} />

        <CompanySeparator margin={0} />
        <AddressAndContactDetailsSection
          openAddressModal={openAddressModal}
          creditReportData={creditReportData}
        />

        <CompanySeparator margin={0} />
        <AdditionalInformationSection creditReportData={creditReportData} />
      </div>
    </>
  );
};

export default CreditReportComponent;
export {
  CompanyIdentificationInformationSection,
  AddressAndContactDetailsSection,
  AdditionalInformationSection,
};
